import _ from "lodash";
import React from "react";
import classes from "./ScoreAssessmentTool.scss";
import classNames from "classnames";
import { TextInput, Tooltip } from "@toddle-edu/ds-web";
import I18nHOC from "UIComponents/I18nHOC";
import AIJustification from "UIComponents/AIJustification";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import {
  updateScoreElementRatingInCache,
  updateScoreAssessmentTool,
} from "ClassRoom/modules/ClassRoomModule";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { CUSTOM_WEIGHTS } from "Constants/stringConstants";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import { getGradeCalculationMethodSetting } from "routes/routes/Administrator/routes/DPAcademicSetup/routes/AssessmentSetup/modules/utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { useFetchQuery } from "Hooks";
import { getAssessmentGradingConfigurationsQuery } from "GradingSetupV2/modules/GradingSetupV2Queries";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import { getIsCustomWeightsV2 } from "ClassRoom/modules/utils";

const styles = {
  inputStyle: {
    width: "120px",
  },
  inputStyle2: {
    width: "100px",
    height: "40px",
  },
  aiEvaluation: {
    border: "1px dashed var(--interactive-tertiary)",
    backgroundColor: `var(--decorative-background-subtle-violet)`,
  },
};

const ScoreAssessmentTool = props => {
  const {
    t,
    isEvaluated,
    scoreValue,
    studentDetails,
    studentAssignmentId,
    assessmentTools,
    showTitle = false,
    isTeacherEvaluationEnabled,
    assignmentDetailsVariables,
    weightage,
    studentCourseTags,
    ibPYPElementRatings,
    updateScoreAssessmentTool,
    isSplitView = false,
    toolStyles,
    addCustomWeights = true,
    isEvaluationEnable,
    academicTermId,
    sourceFrom,
    isAutoSaveDisabled = false,
    updateScoreLocal,
    innerContainerClassName,
    textInputContainerClassName,
    showMaxScoreInPlaceholder = false,
    handleRefetchStudentRatings,
    showUpdatedDesign = false,
    getInputRef,
    isAiEvaluationEnabled,
    onAcceptAIEvaluation,
    isGeneratingAIResponse,
    showUpdatedDesignForAIGrading,
    userType,
    collapseJustification = true,
    assessmentId,
    isUsingNewGradingSetupInCourses,
  } = props;

  let { isCustomWeights = false } = props;

  const {
    scoreText = {},
    labelStyle = {},
    scoreSubText = {},
    scoreTextInputStyle = {},
  } = toolStyles || {};

  const isStaff = userType === "staff";

  const { maxScore, isScoreEnabled, scoreFieldId } = getScoreDetailsMemoize({
    assessmentTools,
  });

  const aiScoreData = _.find(ibPYPElementRatings, {
    ibPYPElementType: "SCORE",
    creatorType: "AI",
  });

  const justificationMessage = _.get(
    aiScoreData,
    "evaluationJustification.message"
  );

  const aiEvaluation = _.find(ibPYPElementRatings, {
    ibPYPElementType: "SCORE",
    creatorType: "AI",
    isDeleted: false,
  });

  const aiEvaluationValue = _.get(aiEvaluation, "value", "");

  const isEvaluatedByAI =
    isAiEvaluationEnabled && !_.isEmpty(aiEvaluationValue);

  const { data: gradingConfigurations } = useFetchQuery({
    query: getAssessmentGradingConfigurationsQuery,
    skipCondition: _.isEmpty(assessmentId) || !isUsingNewGradingSetupInCourses,
    variables: {
      id: assessmentId,
    },
    parseData: data => _.get(data, "node.gradingConfigurations", []),
  });

  if (isUsingNewGradingSetupInCourses) {
    isCustomWeights = getIsCustomWeightsV2(gradingConfigurations);
  }

  const updateScore = e => {
    const score = _.get(e, "target.value", "");

    updateScoreAssessmentTool({
      score,
      maxScore,
      assignmentDetailsVariables,
      studentDetails,
      studentAssignmentId,
      studentCourseTags,
      assessmentTools,
      ibPYPElementRatings,
      scoreFieldId,
      academicTermId,
      sourceFrom,
      isAutoSaveDisabled,
      updateScoreLocal,
      isEvaluated,
    });
  };

  // optimistic update on blur
  const onBlur = () => {
    if (sourceFrom === "GRADEBOOK" && handleRefetchStudentRatings) {
      handleRefetchStudentRatings();
    }

    if (_.includes(scoreValue, ".")) {
      updateScoreElementRatingInCache({
        assignmentDetailsVariables,
        scoreValue: _.toString(parseFloat(scoreValue)),
      });
    }
  };

  const acceptAIEvaluatedScore = () => {
    if (isEvaluatedByAI) {
      onAcceptAIEvaluation({ academicTermId });
    }
  };

  const getScoreEvaluationItems = () => {
    // default weightage is 100
    const assessmentWeightage = _.isFinite(weightage) ? weightage : 100;
    const weight = _.round((scoreValue / maxScore) * assessmentWeightage, 2);

    const taskWeight =
      isCustomWeights && addCustomWeights
        ? [
            {
              id: "TASK_WEIGHT",
              label: t("academicSetup:task_weight"),
              placeholder: "0",
              value: scoreValue ? _.toString(weight) : "",
              maxValue: assessmentWeightage,
              isDisabled: true,
            },
          ]
        : [];

    const scorePlaceholder = showMaxScoreInPlaceholder
      ? t("common:max_score_with_score", { score: maxScore })
      : t("common:eg_label", {
          label: maxScore,
        });

    return [
      {
        id: "MAX_SCORE",
        label: t("common:score"),
        placeholder: scorePlaceholder,
        value: scoreValue ? scoreValue : "",
        maxValue: maxScore,
        isDisabled: !isScoreEnabled || isEvaluated,
      },
      ...taskWeight,
    ];
  };

  const getScoreSplitViewEvaluationDetails = () => {
    const scoreTitleClasses = classNames({
      [classes.scoreTitle]: !showUpdatedDesignForAIGrading,
      ["text-label-l"]: !showUpdatedDesign && !showUpdatedDesignForAIGrading,
      ["text-label-3"]: showUpdatedDesign,
      [classes.scoreTitle2]: showUpdatedDesign,
      ["text-label"]: showUpdatedDesignForAIGrading,
    });
    const scoreSubTitleClasses = classNames(
      classes.scoreSubTitle,
      "text-body-3"
    );
    const leftContainerClasses = classNames(classes.leftContainer, {
      [classes.leftContainer2]: showUpdatedDesign,
    });
    const rightContainerClasses = classNames(classes.rightContainer, {
      [classes.aiEvaluationInput]: isEvaluatedByAI,
    });

    const textInputStyle =
      showUpdatedDesign || isAiEvaluationEnabled
        ? styles.inputStyle2
        : styles.inputStyle;

    return (
      <div className={classes.splitViewContainer}>
        <div className={leftContainerClasses}>
          <div
            className={scoreTitleClasses}
            style={!isAiEvaluationEnabled ? scoreText : {}}
          >
            {t("common:score")}
          </div>
          <div className={scoreSubTitleClasses} style={scoreSubText}>
            {t("common:capitalize", {
              text: t("common:out_of_with_label", {
                label: maxScore || "-",
              }),
            })}
          </div>
        </div>

        <ConditionalWrapper
          type="wrapperAsElement"
          condition={isEvaluatedByAI}
          wrapper={<Tooltip tooltip={t("ai:click_to_approve_score")} />}
        >
          <div className={rightContainerClasses}>
            {isGeneratingAIResponse ? (
              <SkeletonLoader width="100px" height="40px" />
            ) : (
              <TextInput
                placeholder={t("common:max_with_count", {
                  count: maxScore || "-",
                })}
                value={scoreValue ? scoreValue : ""}
                onChange={updateScore}
                onBlur={onBlur}
                style={{
                  ...textInputStyle,
                  ...(isEvaluatedByAI ? styles.aiEvaluation : {}),
                }}
                disabled={
                  !isScoreEnabled || !isEvaluationEnable || _.isEmpty(maxScore)
                }
                size={showUpdatedDesign ? "medium" : "large"}
                dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
                ref={getInputRef}
                onFocus={acceptAIEvaluatedScore}
              />
            )}
          </div>
        </ConditionalWrapper>
      </div>
    );
  };

  const getScoreEvaluationDetails = () => {
    const maxMarkTextClass = classNames(classes.maxMarkText, "text-label-2");

    const innerContainerClasses = classNames(classes.innerContainer, {
      [innerContainerClassName]: !_.isEmpty(innerContainerClassName),
    });

    const textInputContainerClasses = classNames({
      [textInputContainerClassName]: !_.isEmpty(textInputContainerClassName),
    });

    const textInputStyles = {
      ...styles.inputStyle,
      ...scoreTextInputStyle,
    };

    return (
      <div className={classes.scoreDetails}>
        {showTitle ? (
          <div className={classes.scoreText} style={scoreText}>
            {t("common:score")}
          </div>
        ) : null}
        {isEvaluated && !scoreValue ? (
          <span>{t("classRoom:not_graded")}</span>
        ) : (
          <div className={classes.interactiveConWrapper}>
            {_.map(getScoreEvaluationItems(), item => {
              const {
                id,
                label,
                placeholder,
                value,
                maxValue,
                isDisabled,
              } = item;

              return (
                <div key={id} className={innerContainerClasses}>
                  <div className="text-label-2" style={labelStyle}>
                    {label}
                  </div>
                  <div className={classes.interactiveContainer}>
                    {isEvaluated ? (
                      <div className={classes.evaluatedScoreText}>{value}</div>
                    ) : (
                      <div className={textInputContainerClasses}>
                        <TextInput
                          placeholder={placeholder}
                          value={value}
                          onChange={updateScore}
                          onBlur={onBlur}
                          style={textInputStyles}
                          disabled={isDisabled}
                        />
                      </div>
                    )}
                    {showMaxScoreInPlaceholder ? null : (
                      <div className={maxMarkTextClass}>
                        {t("common:out_of_with_label", {
                          label: maxValue,
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const aiScoreValue = _.get(aiScoreData, "value", "");
  const aiEvaluationLabel = aiScoreValue && `${aiScoreData?.value}/${maxScore}`;

  return isScoreEnabled && isTeacherEvaluationEnabled ? (
    <div className={classes.scoreContainer}>
      {isSplitView
        ? getScoreSplitViewEvaluationDetails()
        : getScoreEvaluationDetails()}

      {isStaff && isAiEvaluationEnabled && !_.isEmpty(justificationMessage) ? (
        <AIJustification
          justifications={[justificationMessage]}
          isAIGeneratingResponse={isGeneratingAIResponse}
          aiEvaluationLabel={aiEvaluationLabel}
          isAIResponseAccepted={aiScoreData?.isDeleted && collapseJustification}
        />
      ) : null}
    </div>
  ) : null;
};

const mapStateToProps = state => {
  const curriculumType = _.get(state, "platform.currentCurriculumProgram.type");
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );
  const academicYearId = getSelectedAcademicYearId(state);

  const gradeCalculationMethodSettingName = getGradeCalculationMethodSetting({
    curriculumType,
  });

  const gradeCalculationMethod = getSettingValue({
    name: gradeCalculationMethodSettingName,
    currentCurriculumProgramId: curriculumProgramId,
  });

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId,
    academicYearId: academicYearId,
  });

  return {
    isCustomWeights: _.isEqual(gradeCalculationMethod, CUSTOM_WEIGHTS),
    curriculumProgramId,
    userType: _.get(state, "login.userInfo.user_type", ""),
    gradeCalculationMethodSettingName,
    isUsingNewGradingSetupInCourses,
  };
};

const mapActionCreators = {
  updateScoreAssessmentTool,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(ScoreAssessmentTool);
